import {
  UseQueryResult,
  useQuery,
  UseQueryOptions,
  useMutation,
} from "react-query"
import { AxiosRequestConfig } from "axios"
import jsFileDownlaod from "js-file-download"
import moment from "moment"

import { APIEndpoints } from "../constants/api-endpoints"
import { HttpRequest, HttpBaseRequest } from "../services/_http"
import { QueryKeys } from "./_query_keys"
import { useNotificationBar } from "context/notificationContext"

/* ========================================================================== */
/*  Types                                                                      */
/* ========================================================================== */

export type ArticleStatus = "draft" | "in_review" | "changes_requested" | "approved" | "published" | "archived"

export type ArticleContentType = "Article" | "BlogPosting" | "NewsArticle"

export type ConnectedContentType = "category" | "show" | "episode" | "collection"

export interface ArticleFaq {
  question: string
  answer: string
}

export interface Article {
  id: number
  title: string
  slug: string
  short_description: string
  author: string
  content_html?: string
  status: ArticleStatus
  review_note?: string | null
  reviewed_at?: string | null
  version: number
  published_at?: string | null
  created_at?: string
  updated_at?: string
  seo_title?: string | null
  meta_description?: string | null
  focus_keyword?: string | null
  search_intent?: string | null
  key_takeaways?: string[] | null
  direct_answer?: string | null
  geo_summary?: string | null
  faqs?: ArticleFaq[] | null
  schema_type?: ArticleContentType | null
  related_articles?: number[] | null
  connected_content?: ArticleConnectedContent[] | null
}

export interface ArticleConnectedContent {
  id: string
  content_type: ConnectedContentType
  content_id: string
  sort_order: number
  name?: string | null
}

export interface ArticleConnectedFormItem {
  contentType: ConnectedContentType
  contentId: string
  name?: string
  thumbnail?: string
}

/** Body for POST /articles, PUT /articles/:id and POST /articles/:id/publish */
export interface ArticlePayload {
  title: string
  slug?: string
  short_description: string
  author: string
  content_html: string
  status?: ArticleStatus
  expected_version?: number
  seo_title?: string | null
  meta_description?: string | null
  focus_keyword?: string | null
  search_intent?: string | null
  key_takeaways?: string[] | null
  direct_answer?: string | null
  geo_summary?: string | null
  faqs?: ArticleFaq[] | null
  schema_type?: ArticleContentType
  related_articles?: number[] | null
  connected_content?: { content_type: ConnectedContentType; content_id: string }[]
}

/** Editor values use display-oriented names. The API still expects its own keys. */
export interface ArticleSeoFormValues {
  seoTitle?: string | null
  metaDescription?: string | null
  focusKeyword?: string | null
  searchIntent?: string | null
  keyTakeaways?: string[] | null
  directAnswer?: string | null
  aiSummary?: string | null
  faqs?: ArticleFaq[] | null
  contentType?: ArticleContentType | null
  relatedArticles?: string | null
}

export interface GenerateArticleSeoRequest {
  article_id?: number
  title?: string
  short_description?: string
  author?: string
  content_html?: string
  language?: string
  target_audience?: string
}

const blankToNull = (value?: string | null) => {
  const text = value?.trim()
  return text ? text : null
}

export const seoFieldsFromForm = (values: ArticleSeoFormValues): Partial<ArticlePayload> => {
  const takeaways = (values.keyTakeaways || []).map((item) => item.trim()).filter(Boolean)
  const faqs = (values.faqs || [])
    .filter((item) => item.question?.trim() || item.answer?.trim())
    .map((item) => ({ question: item.question.trim(), answer: item.answer.trim() }))
  const related = (values.relatedArticles || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => Number(item))

  return {
    seo_title: blankToNull(values.seoTitle),
    meta_description: blankToNull(values.metaDescription),
    focus_keyword: blankToNull(values.focusKeyword),
    search_intent: blankToNull(values.searchIntent),
    key_takeaways: takeaways.length ? takeaways : null,
    direct_answer: blankToNull(values.directAnswer),
    geo_summary: blankToNull(values.aiSummary),
    faqs: faqs.length ? faqs : null,
    schema_type: values.contentType || "BlogPosting",
    related_articles: related.length ? related : null,
  }
}

const CONNECTED_TYPES: ConnectedContentType[] = ["category", "show", "episode", "collection"]

export const connectedContentFromForm = (
  items?: { contentType?: string | null; contentId?: string | null }[] | null
): Pick<ArticlePayload, "connected_content"> => ({
  connected_content: (items || [])
    .filter((item): item is { contentType: ConnectedContentType; contentId: string } =>
      Boolean(item.contentId) && CONNECTED_TYPES.includes(item.contentType as ConnectedContentType)
    )
    .map((item) => ({
      content_type: item.contentType,
      content_id: item.contentId,
    })),
})

/**
 * The API returns either the entity itself or wraps it in `results` / `data`.
 * Normalise so callers always get the article (or list) back.
 */
export const unwrapResponse = (res: any) => {
  const body = res?.data
  return body?.results ?? body?.data ?? body
}

/** Build the full payload the API expects from a stored article. */
export const toArticlePayload = (
  article: Partial<Article>,
  overrides: Partial<ArticlePayload> = {}
): ArticlePayload => ({
  title: article.title ?? "",
  slug: article.slug,
  short_description: article.short_description ?? "",
  author: article.author ?? "",
  content_html: article.content_html ?? "",
  status: article.status,
  expected_version: article.version,
  seo_title: article.seo_title ?? null,
  meta_description: article.meta_description ?? null,
  focus_keyword: article.focus_keyword ?? null,
  search_intent: article.search_intent ?? null,
  key_takeaways: article.key_takeaways ?? null,
  direct_answer: article.direct_answer ?? null,
  geo_summary: article.geo_summary ?? null,
  faqs: article.faqs ?? null,
  schema_type: article.schema_type || "BlogPosting",
  related_articles: article.related_articles ?? null,
  ...(Array.isArray(article.connected_content)
    ? {
        connected_content: article.connected_content.map((item) => ({
          content_type: item.content_type,
          content_id: item.content_id,
        })),
      }
    : {}),
  ...overrides,
})

/* ========================================================================== */
/*  Article CRUD  (see "Article and Media API Guide")                          */
/* ========================================================================== */

// GET /articles?search=&status=&pagination[...]=&sort[...]=
export const useGetArticleData = (
  searchOptions?: any,
  options?: UseQueryOptions<any, any, any, any>
): UseQueryResult<any> =>
  useQuery({
    queryKey: [QueryKeys.ARTICLES, searchOptions],
    queryFn: () =>
      HttpRequest({
        url: APIEndpoints.Articles,
        params: searchOptions,
        method: "GET",
      }),
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    ...(options as any),
  })

// GET /articles/:id  (full content_html)
export const useGetArticleDetails = (
  id: any,
  options?: UseQueryOptions<any, any, any, any>
): UseQueryResult<any> =>
  useQuery({
    queryKey: [QueryKeys.ARTICLE_DETAIL, id],
    queryFn: () =>
      HttpRequest({
        url: `${APIEndpoints.Articles}/${id}`,
        method: "GET",
      }),
    enabled: !!id,
    refetchOnWindowFocus: false,
    cacheTime: 0,
    ...(options as any),
  })

// POST /articles  (always creates a draft)
export const useCreateArticle = (options?: any) =>
  useMutation(
    ({ data }: { data: ArticlePayload }) =>
      HttpRequest({
        url: APIEndpoints.Articles,
        method: "POST",
        data: { ...data, status: "draft" },
      }),
    options
  )

// PUT /articles/:id  (full payload + expected_version)
export const useUpdateArticle = (options?: any) =>
  useMutation(
    ({ id, data }: { id: number | string; data: ArticlePayload }) =>
      HttpRequest({
        url: `${APIEndpoints.Articles}/${id}`,
        method: "PUT",
        data,
      }),
    options
  )

// POST /articles/seo/generate  (suggestions are not saved until the article is submitted)
export const useGenerateArticleSeo = (options?: any) =>
  useMutation(
    (data: GenerateArticleSeoRequest) =>
      HttpRequest({
        url: `${APIEndpoints.Articles}/seo/generate`,
        method: "POST",
        data,
      }),
    options
  )

// POST /articles/:id/submit  (keeps the saved content and moves it into review)
export const useSubmitArticle = (options?: any) =>
  useMutation(
    ({ id, expected_version }: { id: number | string; expected_version?: number }) =>
      HttpRequest({
        url: `${APIEndpoints.Articles}/${id}/submit`,
        method: "POST",
        data: { expected_version },
      }),
    options
  )

// POST /articles/:id/review
export const useReviewArticle = (options?: any) =>
  useMutation(
    ({
      id,
      decision,
      note,
      expected_version,
    }: {
      id: number | string
      decision: "approved" | "changes_requested"
      note?: string
      expected_version?: number
    }) =>
      HttpRequest({
        url: `${APIEndpoints.Articles}/${id}/review`,
        method: "POST",
        data: { decision, note, expected_version },
      }),
    options
  )

// POST /articles/:id/publish  (only after approval)
export const usePublishArticle = (options?: any) =>
  useMutation(
    ({ id, data }: { id: number | string; data: Partial<ArticlePayload> }) =>
      HttpRequest({
        url: `${APIEndpoints.Articles}/${id}/publish`,
        method: "POST",
        data,
      }),
    options
  )

// DELETE /articles/:id
// NOTE: the API guide does not document a delete route. This assumes the
// conventional REST one. If the backend has none, remove the Delete action
// from ArticleList and use "Archive" instead.
export const useDeleteArticle = (options?: any) =>
  useMutation(
    ({ id }: { id: any }) =>
      HttpRequest({
        url: `${APIEndpoints.Articles}/${id}`,
        method: "DELETE",
      }),
    options
  )

/* ========================================================================== */
/*  Legacy hooks — unchanged, kept so other modules that import them still     */
/*  compile. Nothing in the article CRUD screens uses these.                   */
/* ========================================================================== */

export const uploadFileToPresignedUrl = (
  uploadUrl: string,
  file: File,
  onUploadProgress?: (e: any) => void
) =>
  HttpBaseRequest({
    url: uploadUrl,
    method: "PUT",
    data: file,
    headers: { "Content-Type": file.type },
    onUploadProgress,
  } as AxiosRequestConfig)

export const useArticlesData = (
  series_id: any,
  searchOptions?: any,
  options?: any
): UseQueryResult<any> => {
  const queryKey = [QueryKeys.ARTICLES, series_id, searchOptions]

  return useQuery({
    queryKey,
    queryFn: () =>
      HttpRequest({
        url: `${APIEndpoints.Articles}`,
        params: searchOptions,
        method: "GET",
      }),
    refetchOnWindowFocus: false,
    keepPreviousData: false,
    ...options,
  })
}

export const useUpdateArticleAssetType = (options?: any) =>
  useMutation(
    ({ data, id }: { data: any; id: any }) =>
      HttpRequest({
        url: `${APIEndpoints.Articles}/asset/${id}`,
        method: "PUT",
        data,
      }),
    options
  )

export const useArticleDetailData = (
  article_id: any,
  mediaType: any,
  entry_id: any,
  options?: UseQueryOptions
): UseQueryResult<any> =>
  useQuery({
    queryKey: [QueryKeys.SERIES_ARTICLE, article_id],
    queryFn: () =>
      HttpRequest({
        url: `${APIEndpoints.SeriesArticle}/${article_id}`,
        method: "GET",
        params: { mediaType, entry_id },
      }),
    refetchOnWindowFocus: false,
    cacheTime: 0,
    ...options,
  })

export const useAddArticle = (options?: any) =>
  useMutation(
    ({ data }: { data: any }) =>
      HttpRequest({
        url: `${APIEndpoints.Articles}`,
        method: "POST",
        data,
      }),
    options
  )

export const useArticleSearch = (searchTerm = "") =>
  useQuery(["articles-search", searchTerm], async () => {
    if (!searchTerm.trim()) return []

    const { data } = await HttpRequest({
      url: `/articles/search/all?search=${encodeURIComponent(searchTerm)}`,
      method: "GET",
    })

    return data
  })

export const fetchArticlesSearchApi = async (search: string) => {
  const { data } = await HttpRequest({
    url: `/articles/search/all?search=${encodeURIComponent(search)}`,
    method: "GET",
  })

  return data?.results
}

export const useArticlesSearch = (searchTerm: string) =>
  useQuery({
    queryKey: ["article-search", searchTerm],
    enabled: Boolean(searchTerm.trim()),
    keepPreviousData: true,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const { data } = await HttpRequest({
        url: `/articles/search/all?search=${encodeURIComponent(searchTerm)}`,
        method: "GET",
      })

      return data
    },
  })

export const useArticlesSearchUnlinked = (searchTerm: string) =>
  useQuery({
    queryKey: ["article-search-unlinked", searchTerm],
    enabled: Boolean(searchTerm.trim()),
    keepPreviousData: true,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const { data } = await HttpRequest({
        url: `/articles/search/unlinked?search=${encodeURIComponent(searchTerm)}`,
        method: "GET",
      })

      return data
    },
  })

export const useExportAllArticles = () => {
  const notification = useNotificationBar()

  return (additionalQueryParams: Object = {}) => {
    const processingIdx = notification.push({
      type: "info",
      title: "Processing file please wait",
      message: "Your data will be downloaded soon ...",
    })

    HttpRequest({
      url: `${APIEndpoints.ExportAllArticles}`,
      method: "GET",
      params: { ...additionalQueryParams },
      responseType: "blob",
    })
      .then((data) => {
        jsFileDownlaod(
          data?.data,
          `articles_${moment().format("DD-MMM-YYYY-hh:mm:ss")}.xlsx`
        )

        if (processingIdx) notification.hide(processingIdx)

        notification.push({
          type: "success",
          title: "Downloaded",
          message: "Exported data has been downloaded successfully!",
        })
      })
      .catch(() => {
        if (processingIdx) notification.hide(processingIdx)

        notification.push({
          type: "error",
          title: "Download failed",
          message: "Unable to export articles. Please try again.",
        })
      })
  }
}